import { create } from "zustand";
import { nanoid } from "nanoid";
import { apiLoadTimeline, apiSaveTimeline, apiCreateTimeline } from "./api";
import { probeMedia } from "./helpers";
import { apiFetch } from "@/lib/api";
import {
  UNDO_REDO_DEPTH,
  captureHistorySnapshot,
  applyHistorySnapshot,
  createHistoryState,
  pushUndoSnapshot,
  beginHistoryTransaction,
  commitHistoryTransaction,
  cancelHistoryTransaction,
  getUndoResult,
  getRedoResult,
} from "./undoRedoHandler";

/**
 * ===== Timeline Model =====
 * time unit: seconds (number). In production use integer microseconds.
 */

export const DEFAULT_TRACKS = [
  { id: "v1", type: "video", name: "Video 1" }, // topmost lane wins (images also go here)
  { id: "v2", type: "video", name: "Video 2" },
  { id: "a1", type: "audio", name: "Audio 1" },
  { id: "a2", type: "audio", name: "Audio 2" },
  { id: "a3", type: "audio", name: "Audio 3" },
];

/**
 * Helper to check if an asset is an image
 * Images are treated like videos but with infinite max duration
 */
export function isImageAsset(asset) {
  return asset?.type === "image" || asset?.isImage === true;
}

/**
 * Get the max duration for an asset
 * Images have infinite max duration, videos/audio have their actual duration
 */
export function getAssetMaxDuration(asset) {
  if (isImageAsset(asset)) {
    return Infinity;
  }
  return asset?.duration || 5;
}

export function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/**
 * Calculate fade gain multiplier for a clip at a given timeline time
 * @param {Object} clip - The clip object with fadeIn, fadeOut, start, duration
 * @param {number} timelineTime - Current time on the timeline
 * @returns {number} Gain multiplier (0-1) accounting for fade in/out
 */
export function getFadeGain(clip, timelineTime) {
  const fadeIn = clip.fadeIn ?? 0;
  const fadeOut = clip.fadeOut ?? 0;
  
  // Ensure fade durations don't exceed clip duration
  const maxFadeIn = Math.min(fadeIn, clip.duration);
  const maxFadeOut = Math.min(fadeOut, clip.duration);
  
  // Time within the clip (0 = start of clip, duration = end of clip)
  const timeInClip = timelineTime - clip.start;
  
  // Fade in: ramp from 0 to 1 over fadeIn seconds (but not exceeding clip duration)
  if (maxFadeIn > 0 && timeInClip < maxFadeIn) {
    return Math.max(0, Math.min(1, timeInClip / maxFadeIn));
  }
  
  // Fade out: ramp from 1 to 0 over fadeOut seconds at the end (but not exceeding clip duration)
  if (maxFadeOut > 0 && timeInClip > clip.duration - maxFadeOut) {
    const timeFromEnd = clip.duration - timeInClip;
    return Math.max(0, Math.min(1, timeFromEnd / maxFadeOut));
  }
  
  // No fade - full gain
  return 1.0;
}

function sortByStart(a, b) {
  return a.start - b.start;
}

function withActionHistory(state) {
  if (state.historyTransaction) return state;
  return pushUndoSnapshot(state, captureHistorySnapshot(state), UNDO_REDO_DEPTH);
}

/**
 * ===== Store =====
 */
export const EDITOR_MODES = {
  SELECT: "select",
  CUT: "cut",
  TRACK_SELECT_FORWARD: "track_select_forward",
};

export const useEditor = create((set, get) => ({
  tracks: DEFAULT_TRACKS,
  assets: {},
  clips: [],
  playhead: 0,
  zoom: 80,
  isPlaying: false,
  mutedTracks: {},
  trackVolumes: {},
  mode: EDITOR_MODES.SELECT,
  selectedClipIds: [],
  snappingEnabled: true,
  snapThreshold: 10,
  previewTime: null,
  assetsFolder: [],
  currentAssetFolderId: null,
  setCurrentAssetFolderId: (id) => set({ currentAssetFolderId: id }),

  // Persistence
  timelineId: null,
  showId: null,
  isLoading: false,
  isSaving: false,
  isDirty: false,
  lastSavedAt: null,
  timelineName: 'Untitled',
  ...createHistoryState(),

  // Audio metering - shared audio context and analyser for audio meters
  audioMeterContext: null, // AudioContext for metering
  audioMeterAnalyserL: null, // AnalyserNode for left channel
  audioMeterAnalyserR: null, // AnalyserNode for right channel
  audioMeterSplitter: null, // ChannelSplitterNode

  episodeId: null,
  segmentId: null,
  scriptItems: [],
  storyboardItems: [],
  setEpisodeId: (id) => set({ episodeId: id }),
  setSegmentId: (id) => set({ segmentId: id }),
  setScriptItems: (items) => set({ scriptItems: items }),
  setStoryboardItems: (items) => set({ storyboardItems: items }),
  updateScriptItem: (scriptId, updates) => set((s) => ({
    scriptItems: s.scriptItems.map((item) =>
      item.scriptId === scriptId ? { ...item, ...updates } : item
    ),
  })),

  // Sketch utilities
  duplicateSketchForClip: async (clipId, assetId, sketchId) => {
    const s = get();
    if (!s.showId || !sketchId) return;
    try {
      const res = await apiFetch(`/api/shows/${s.showId}/sketches/${sketchId}`);
      if (!res.ok) return;
      const original = await res.json();
      const { _id, createdAt, updatedAt, __v, ...sketchData } = original;
      const createRes = await apiFetch(`/api/shows/${s.showId}/sketches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sketchData),
      });
      if (!createRes.ok) return;
      const newSketch = await createRes.json();
      if (newSketch._id) {
        get().updateClip(clipId, { sketchId: newSketch._id });
        if (assetId) {
          get().updateAsset(assetId, { sketchId: newSketch._id });
        }
      }
    } catch (e) {
      console.error('Failed to duplicate sketch:', e);
    }
  },

  // Sketch editor state
  sketchEditorClipId: null,
  // Last known horizontal scroll of the timeline lanes; persisted across
  // unmounts so re-entering the timeline (e.g. after closing the sketch
  // editor) restores the previous viewport instead of jumping to 0.
  timelineScrollLeft: 0,
  setTimelineScrollLeft: (value) => set({ timelineScrollLeft: value }),
  openSketchEditor: (clipId) => {
    const s = get();
    const clip = s.clips.find((c) => c.id === clipId);
    if (!clip) return;
    set({ sketchEditorClipId: clipId });
  },
  closeSketchEditor: () => set({ sketchEditorClipId: null }),
  handleSketchSaveComplete: ({ sketchId: savedSketchId, imageUrl }) => {
    const s = get();
    const clipId = s.sketchEditorClipId;
    if (!clipId) return;
    const clip = s.clips.find((c) => c.id === clipId);
    if (!clip) return;
    const asset = clip.assetId ? s.assets[clip.assetId] : null;

    if (!clip.assetId) {
      const newAssetId = nanoid();
      s.addAsset({
        id: newAssetId,
        url: imageUrl,
        type: "image",
        isImage: true,
        duration: 5,
        name: "Sketch",
        thumbnailUrl: imageUrl,
        sketchId: savedSketchId,
      });
      s.updateClip(clip.id, { assetId: newAssetId });
      s.addAssetFolderItem({
        id: nanoid(),
        parentId: null,
        type: "upload",
        mediaType: "image",
        name: "Sketch",
        url: imageUrl,
        thumbnailUrl: imageUrl,
        canDelete: true,
      });
      set({ sketchEditorClipId: null });
      get().saveToDB();
    } else {
      const assetUpdates = { url: imageUrl, thumbnailUrl: imageUrl, sketchId: savedSketchId };
      s.updateAsset(clip.assetId, assetUpdates);
      const matchingItem = s.assetsFolder.find((i) => i.url === asset?.url);
      if (matchingItem) {
        s.updateAssetFolderItem(matchingItem.id, { url: imageUrl, thumbnailUrl: imageUrl });
      }
      set({ sketchEditorClipId: null });
      get().saveToDB();
    }
  },

  setPlayhead: (t) => set({ playhead: Math.max(0, t) }),
  setPreviewTime: (t) => set({ previewTime: t }), // null clears preview
  setZoom: (z) => set({ zoom: clamp(z, 30, 300) }),
  setPlaying: (v) => set({ isPlaying: v }),
  
  // Audio metering setup
  setAudioMeterNodes: (nodes) => set({
    audioMeterContext: nodes.context,
    audioMeterAnalyserL: nodes.analyserL,
    audioMeterAnalyserR: nodes.analyserR,
    audioMeterSplitter: nodes.splitter,
  }),
  setMode: (mode) => set({ mode }),
  beginHistoryTransaction: (label = "edit") =>
    set((s) => beginHistoryTransaction(s, label, captureHistorySnapshot(s))),
  commitHistoryTransaction: () =>
    set((s) => commitHistoryTransaction(s, captureHistorySnapshot(s), UNDO_REDO_DEPTH)),
  cancelHistoryTransaction: () =>
    set((s) => cancelHistoryTransaction(s)),
  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,
  undo: () =>
    set((s) => {
      const result = getUndoResult(s, captureHistorySnapshot(s), UNDO_REDO_DEPTH);
      if (!result) return s;
      const restored = applyHistorySnapshot(s, result.snapshot);
      return {
        ...restored,
        undoStack: result.undoStack,
        redoStack: result.redoStack,
        historyTransaction: null,
        isDirty: true,
      };
    }),
  redo: () =>
    set((s) => {
      const result = getRedoResult(s, captureHistorySnapshot(s), UNDO_REDO_DEPTH);
      if (!result) return s;
      const restored = applyHistorySnapshot(s, result.snapshot);
      return {
        ...restored,
        undoStack: result.undoStack,
        redoStack: result.redoStack,
        historyTransaction: null,
        isDirty: true,
      };
    }),
  setSelectedClip: (clipId) => set({ selectedClipIds: clipId ? [clipId] : [] }),
  addToSelection: (clipId) =>
    set((s) => {
      if (!clipId || s.selectedClipIds.includes(clipId)) return s;
      return { selectedClipIds: [...s.selectedClipIds, clipId] };
    }),
  removeFromSelection: (clipId) =>
    set((s) => ({
      selectedClipIds: s.selectedClipIds.filter((id) => id !== clipId),
    })),
  toggleSelection: (clipId) =>
    set((s) => {
      if (!clipId) return s;
      const index = s.selectedClipIds.indexOf(clipId);
      if (index === -1) {
        return { selectedClipIds: [...s.selectedClipIds, clipId] };
      } else {
        return { selectedClipIds: s.selectedClipIds.filter((id) => id !== clipId) };
      }
    }),
  clearSelection: () => set({ selectedClipIds: [] }),
  toggleSnapping: () => set((s) => ({ snappingEnabled: !s.snappingEnabled })),
  setSnapping: (enabled) => set({ snappingEnabled: enabled }),
  removeClip: (clipId) =>
    set((s) => {
      if (!s.clips.some((c) => c.id === clipId)) return s;
      const base = withActionHistory(s);
      return {
        ...base,
        clips: base.clips.filter((c) => c.id !== clipId),
        selectedClipIds: base.selectedClipIds.filter((id) => id !== clipId),
        isDirty: true,
      };
    }),
  removeClips: (clipIds) =>
    set((s) => {
      if (!clipIds?.length) return s;
      const hasAny = s.clips.some((c) => clipIds.includes(c.id));
      if (!hasAny) return s;
      const base = withActionHistory(s);
      return {
        ...base,
        clips: base.clips.filter((c) => !clipIds.includes(c.id)),
        selectedClipIds: base.selectedClipIds.filter((id) => !clipIds.includes(id)),
        isDirty: true,
      };
    }),
  toggleTrackMute: (trackId) =>
    set((s) => ({
      mutedTracks: { ...s.mutedTracks, [trackId]: !s.mutedTracks[trackId] },
      isDirty: true,
    })),
  setTrackVolume: (trackId, volume) =>
    set((s) => {
      const current = s.trackVolumes[trackId] ?? 1;
      if (current === volume) return s;
      const base = withActionHistory(s);
      return {
        ...base,
        trackVolumes: { ...base.trackVolumes, [trackId]: volume },
        mutedTracks: { ...base.mutedTracks, [trackId]: volume === 0 },
        isDirty: true,
      };
    }),

  renameTrack: (trackId, newName) =>
    set((s) => {
      const track = s.tracks.find((t) => t.id === trackId);
      if (!track || track.name === newName) return s;
      const base = withActionHistory(s);
      return {
        ...base,
        tracks: base.tracks.map((t) => (t.id === trackId ? { ...t, name: newName } : t)),
        isDirty: true,
      };
    }),

  findAssetByScriptId: (scriptId) => {
    if (!scriptId) return null;
    const { assets } = get();
    return Object.values(assets).find((a) => a.scriptId === scriptId) || null;
  },

  addAsset: (asset) => set((s) => ({ assets: { ...s.assets, [asset.id]: asset }, isDirty: true })),
  removeAsset: (assetId) => set((s) => {
    const newAssets = { ...s.assets };
    delete newAssets[assetId];
    return { assets: newAssets, isDirty: true };
  }),
  updateAsset: (assetId, updates, options = {}) =>
    set((s) => {
      const asset = s.assets[assetId];
      if (!asset) return s;
      const merged = { ...asset, ...updates };
      const changed = JSON.stringify(asset) !== JSON.stringify(merged);
      if (!changed) return s;
      const base = options.recordHistory ? withActionHistory(s) : s;
      return {
        ...base,
        assets: { ...base.assets, [assetId]: merged },
        isDirty: true,
      };
    }),

  updateScriptAsset: (assetId, updates) =>
    set((s) => {
      const asset = s.assets[assetId];
      if (!asset) return s;
      const newAssets = { ...s.assets, [assetId]: { ...asset, ...updates } };
      let newClips = s.clips;
      if (updates.duration != null) {
        newClips = s.clips.map((c) => {
          if (c.assetId !== assetId) return c;
          return { ...c, in: 0, duration: updates.duration };
        });
      }
      return { assets: newAssets, clips: newClips.sort(sortByStart), isDirty: true };
    }),
  // Update asset duration when we discover the actual duration from the media element
  // Note: This does NOT apply to images - they have infinite max duration
  updateAssetDuration: (assetId, actualDuration) =>
    set((s) => {
      const asset = s.assets[assetId];
      if (!asset || !Number.isFinite(actualDuration) || actualDuration <= 0) return s;
      
      // Don't update duration for images - they have effectively infinite duration
      if (isImageAsset(asset)) return s;
      
      // Only update if duration changed significantly (more than 50ms difference)
      if (Math.abs((asset.duration || 0) - actualDuration) < 0.05) return s;
      
      console.log(`Updating asset ${assetId} duration: ${asset.duration} -> ${actualDuration}`);
      
      // Update asset duration
      const newAssets = { ...s.assets, [assetId]: { ...asset, duration: actualDuration } };
      
      // Also update any clips using this asset that haven't been trimmed
      // (where clip.duration matches the old asset.duration)
      const newClips = s.clips.map((c) => {
        if (c.assetId !== assetId) return c;
        // If clip's duration matches old asset duration (hasn't been trimmed), update it
        if (Math.abs(c.duration - (asset.duration || 5)) < 0.1) {
          return { ...c, duration: actualDuration };
        }
        // If clip duration exceeds actual duration, clamp it
        if (c.in + c.duration > actualDuration) {
          return { ...c, duration: Math.max(0.1, actualDuration - c.in) };
        }
        return c;
      });
      
      return { assets: newAssets, clips: newClips };
    }),

  // Assets folder operations
  addAssetFolderItem: (item) =>
    set((s) => ({
      assetsFolder: [...s.assetsFolder, item],
      isDirty: true,
    })),
  getAssetFolderDescendants: (itemId) => {
    const s = get();
    const idsToRemove = new Set();
    const collectDescendants = (parentId) => {
      idsToRemove.add(parentId);
      s.assetsFolder
        .filter((i) => i.parentId === parentId)
        .forEach((child) => collectDescendants(child.id));
    };
    collectDescendants(itemId);
    return s.assetsFolder.filter((i) => idsToRemove.has(i.id));
  },

  getClipsForAssetFolderItems: (folderItems) => {
    const s = get();
    const urls = new Set(folderItems.filter((i) => i.url).map((i) => i.url));
    if (urls.size === 0) return [];
    const matchingAssetIds = Object.values(s.assets)
      .filter((a) => urls.has(a.url))
      .map((a) => a.id);
    return s.clips.filter((c) => matchingAssetIds.includes(c.assetId));
  },

  removeAssetFolderItem: (itemId) =>
    set((s) => {
      const idsToRemove = new Set();
      const collectDescendants = (parentId) => {
        idsToRemove.add(parentId);
        s.assetsFolder
          .filter((i) => i.parentId === parentId)
          .forEach((child) => collectDescendants(child.id));
      };
      collectDescendants(itemId);
      return {
        assetsFolder: s.assetsFolder.filter((i) => !idsToRemove.has(i.id)),
        isDirty: true,
      };
    }),

  removeAssetFolderItemWithClips: (itemId) =>
    set((s) => {
      const idsToRemove = new Set();
      const collectDescendants = (parentId) => {
        idsToRemove.add(parentId);
        s.assetsFolder
          .filter((i) => i.parentId === parentId)
          .forEach((child) => collectDescendants(child.id));
      };
      collectDescendants(itemId);

      const removedItems = s.assetsFolder.filter((i) => idsToRemove.has(i.id));
      const urls = new Set(removedItems.filter((i) => i.url).map((i) => i.url));

      const assetIdsToRemove = new Set();
      if (urls.size > 0) {
        Object.values(s.assets).forEach((a) => {
          if (urls.has(a.url)) assetIdsToRemove.add(a.id);
        });
      }

      const newAssets = { ...s.assets };
      assetIdsToRemove.forEach((id) => delete newAssets[id]);

      return {
        assetsFolder: s.assetsFolder.filter((i) => !idsToRemove.has(i.id)),
        clips: s.clips.filter((c) => !assetIdsToRemove.has(c.assetId)),
        selectedClipIds: s.selectedClipIds.filter((id) => !s.clips.some((c) => c.id === id && assetIdsToRemove.has(c.assetId))),
        assets: newAssets,
        isDirty: true,
      };
    }),
  updateAssetFolderItem: (itemId, updates) =>
    set((s) => ({
      assetsFolder: s.assetsFolder.map((i) =>
        i.id === itemId ? { ...i, ...updates } : i
      ),
      isDirty: true,
    })),
  moveAssetFolderItem: (itemId, newParentId) =>
    set((s) => ({
      assetsFolder: s.assetsFolder.map((i) =>
        i.id === itemId ? { ...i, parentId: newParentId } : i
      ),
      isDirty: true,
    })),

  addClip: ({ assetId, trackId, start, allowTrimming = true, allowSplit = true, mediaWidth, mediaHeight }) =>
    set((s) => {
      const base = withActionHistory(s);
      if (assetId === null) {
        const track = base.tracks.find((t) => t.id === trackId);
        if (!track) return s;
        
        const clip = {
          id: nanoid(),
          assetId: null,
          trackId,
          start,
          in: 0,
          duration: 5,
          gain: 1,
          fadeIn: 0,
          fadeOut: 0,
          allowTrimming,
          allowSplit,
        }

        return {
          ...base,
          clips: [...base.clips, clip].sort(sortByStart),
          selectedClipIds: [clip.id],
          isDirty: true,
        }
      }
      
      const asset = base.assets[assetId];
      if (!asset) return s;
      
      const duration = Math.max(0.1, asset.duration || 5);
      const isVisual = isImageAsset(asset) || asset.type === 'video';
      
      const clip = {
        id: nanoid(),
        assetId,
        trackId,
        start,
        in: 0,
        duration,
        gain: isImageAsset(asset) ? 0 : 1,
        fadeIn: 0,
        fadeOut: 0,
        allowTrimming,
        allowSplit,
        ...(isVisual ? {
          mediaWidth: mediaWidth ?? null,
          mediaHeight: mediaHeight ?? null,
        } : {}),
      }

      return { 
        ...base,
        clips: [...base.clips, clip].sort(sortByStart),
        selectedClipIds: [clip.id],
        isDirty: true,
      }
    }),

  addEmptyClipToTrack: (trackId) =>
    set((s) => {
      const base = withActionHistory(s);
      const track = base.tracks.find((t) => t.id === trackId);
      if (!track) return s;
      
      const clip = {
        id: nanoid(),
        assetId: null,
        trackId,
        start: s.playhead,
        in: 0,
        duration: 5,
        gain: 1,
        fadeIn: 0,
        fadeOut: 0,
        allowTrimming: true,
        allowSplit: true,
      };

      return { 
        ...base,
        clips: [...base.clips, clip].sort(sortByStart),
        selectedClipIds: [clip.id],
        isDirty: true,
      };
    }),

  moveClip: (clipId, patch) =>
    set((s) => {
      if (!s.clips.some((c) => c.id === clipId)) return s;
      const base = withActionHistory(s);
      const clips = base.clips.map((c) => (c.id === clipId ? { ...c, ...patch } : c));
      return { ...base, clips: clips.sort(sortByStart), isDirty: true };
    }),
  
  updateClip: (clipId, updates, options = {}) =>
    set((s) => {
      const clipIndex = s.clips.findIndex((c) => c.id === clipId);
      if (clipIndex === -1) return s; // Clip not found
      const trackedKeys = ["fadeIn", "fadeOut", "gain", "mediaWidth", "mediaHeight", "mediaPosX", "mediaPosY", "mediaAspectLocked"];
      const isUndoable =
        options.recordHistory === true ||
        Object.keys(updates || {}).some((k) => trackedKeys.includes(k));
      const base = isUndoable ? withActionHistory(s) : s;
      
      const updatedClip = { ...base.clips[clipIndex], ...updates };
      const clips = [...base.clips];
      clips[clipIndex] = updatedClip;
      
      return { ...base, clips: clips.sort(sortByStart), isDirty: true };
    }),
    
  moveClips: (clipIds, deltaStart, deltaTrackId, initialPositions, preserveTracks = false) =>
    set((s) => {
      if (!clipIds?.length) return s;
      const base = withActionHistory(s);
      const clips = base.clips.map((c) => {
        if (clipIds.includes(c.id)) {
          // Find initial position for this clip
          const initialPos = initialPositions?.find(p => p.id === c.id);
          // Use initial position + delta to maintain relative distances
          const newStart = initialPos ? initialPos.start + deltaStart : c.start + deltaStart;
          const updates = { start: Math.max(0, newStart) };
          
          // If preserveTracks is true, keep each clip's original track
          // Otherwise, apply deltaTrackId to all clips (single clip drag)
          if (preserveTracks) {
            // Keep original track from initial position
            if (initialPos) {
              updates.trackId = initialPos.trackId;
            }
          } else if (deltaTrackId !== undefined) {
            // Apply new track to all clips (single clip drag)
            updates.trackId = deltaTrackId;
          }
          
          return { ...c, ...updates };
        }
        return c;
      });
      return { ...base, clips: clips.sort(sortByStart), isDirty: true };
    }),

  splitClipAtPlayhead: () =>
    set((s) => {
      const { playhead, clips, assets } = s;
      const candidates = clips
        .filter((c) => playhead > c.start && playhead < c.start + c.duration)
        .sort((a, b) => {
          const ta = s.tracks.findIndex((t) => t.id === a.trackId);
          const tb = s.tracks.findIndex((t) => t.id === b.trackId);
          return ta - tb;
        });

      const target = candidates[0];
      if (!target) return s;

      const asset = assets[target.assetId];
      if (!asset) return s;

      const splitOffset = playhead - target.start;
      if (splitOffset <= 0.05 || splitOffset >= target.duration - 0.05) return s;

      const base = withActionHistory(s);

      const isScript = !!asset.scriptId;
      const hasSketch = !!asset.sketchId;
      let newAssets = base.assets;
      let newAssetsFolder = base.assetsFolder;
      let leftAssetId = target.assetId;
      let rightAssetId = target.assetId;

      if (isScript) {
        const baseAudio = {
          type: asset.type,
          url: asset.url,
          duration: asset.duration,
          name: asset.name,
          mediaType: asset.mediaType || "audio",
          ...(asset.thumbnailUrl ? { thumbnailUrl: asset.thumbnailUrl } : {}),
        };
        const leftId = nanoid();
        const rightId = nanoid();
        leftAssetId = leftId;
        rightAssetId = rightId;
        newAssets = {
          ...base.assets,
          [leftId]: { id: leftId, ...baseAudio },
          [rightId]: { id: rightId, ...baseAudio },
        };
      } else if (hasSketch) {
        const newRightAssetId = nanoid();
        rightAssetId = newRightAssetId;
        newAssets = {
          ...base.assets,
          [newRightAssetId]: {
            ...asset,
            id: newRightAssetId,
            name: asset.name || 'Sketch',
          },
        };
        newAssetsFolder = [
          ...base.assetsFolder,
          {
            id: nanoid(),
            parentId: null,
            type: "upload",
            mediaType: "image",
            name: asset.name || 'Sketch',
            url: asset.url,
            thumbnailUrl: asset.thumbnailUrl || asset.url,
            canDelete: true,
          },
        ];
      }

      const rightClipId = nanoid();
      const left = {
        ...target,
        assetId: leftAssetId,
        duration: splitOffset,
        allowTrimming: true,
        allowSplit: true,
      };
      const right = {
        ...target,
        id: rightClipId,
        assetId: rightAssetId,
        start: playhead,
        in: target.in + splitOffset,
        duration: target.duration - splitOffset,
        allowTrimming: true,
        allowSplit: true,
      };

      if (hasSketch) {
        get().duplicateSketchForClip(rightClipId, rightAssetId, asset.sketchId);
      }

      const next = base.clips.filter((c) => c.id !== target.id);
      next.push(left, right);
      return { ...base, clips: next.sort(sortByStart), assets: newAssets, assetsFolder: newAssetsFolder, isDirty: true };
    }),

  // Split a specific clip at a given time position (for cut mode)
  splitClipAtPosition: (clipId, splitTime) =>
    set((s) => {
      const { clips, assets } = s;
      const target = clips.find((c) => c.id === clipId);
      if (!target) return s;

      const asset = assets[target.assetId];
      if (!asset) return s;

      const splitOffset = splitTime - target.start;
      if (splitOffset <= 0.05 || splitOffset >= target.duration - 0.05) return s;

      const base = withActionHistory(s);

      const isScript = !!asset.scriptId;
      const hasSketch = !!asset.sketchId;
      let newAssets = base.assets;
      let newAssetsFolder = base.assetsFolder;
      let leftAssetId = target.assetId;
      let rightAssetId = target.assetId;

      if (isScript) {
        const baseAudio = {
          type: asset.type,
          url: asset.url,
          duration: asset.duration,
          name: asset.name,
          mediaType: asset.mediaType || "audio",
          ...(asset.thumbnailUrl ? { thumbnailUrl: asset.thumbnailUrl } : {}),
        };
        const leftId = nanoid();
        const rightId = nanoid();
        leftAssetId = leftId;
        rightAssetId = rightId;
        newAssets = {
          ...base.assets,
          [leftId]: { id: leftId, ...baseAudio },
          [rightId]: { id: rightId, ...baseAudio },
        };
      } else if (hasSketch) {
        const newRightAssetId = nanoid();
        rightAssetId = newRightAssetId;
        newAssets = {
          ...base.assets,
          [newRightAssetId]: {
            ...asset,
            id: newRightAssetId,
            name: asset.name || 'Sketch',
          },
        };
        newAssetsFolder = [
          ...base.assetsFolder,
          {
            id: nanoid(),
            parentId: null,
            type: "upload",
            mediaType: "image",
            name: asset.name || 'Sketch',
            url: asset.url,
            thumbnailUrl: asset.thumbnailUrl || asset.url,
            canDelete: true,
          },
        ];
      }

      const rightClipId = nanoid();
      const left = {
        ...target,
        assetId: leftAssetId,
        duration: splitOffset,
        allowTrimming: true,
        allowSplit: true,
      };
      const right = {
        ...target,
        id: rightClipId,
        assetId: rightAssetId,
        start: splitTime,
        in: target.in + splitOffset,
        duration: target.duration - splitOffset,
        allowTrimming: true,
        allowSplit: true,
      };

      if (hasSketch) {
        get().duplicateSketchForClip(rightClipId, rightAssetId, asset.sketchId);
      }

      const next = base.clips.filter((c) => c.id !== target.id);
      next.push(left, right);
      return { ...base, clips: next.sort(sortByStart), assets: newAssets, assetsFolder: newAssetsFolder, isDirty: true };
    }),
    
  setShowId: (id) => set({ showId: id }),
  setTimelineId: (id) => set({ timelineId: id }),
  setTimelineName: (name) => set({ timelineName: name, isDirty: true }),
  markDirty: () => set({ isDirty: true }),

  loadFromDB: async (showId, timelineId) => {
    set({ isLoading: true, showId, timelineId });
    try {
      const data = await apiLoadTimeline(showId, timelineId);
      const clips = (data.clips || []).map(clip => ({
        ...clip,
        allowTrimming: clip.allowTrimming !== false,
        allowSplit: clip.allowSplit !== false,
      }));
      const assets = data.assets || {};
      const episodeId = data.episode || null;
      const segmentId = data.segment || null;

      // Resolve assets/clips with duration -1 (videos added from storyboard
      // whose real duration wasn't known server-side at creation time).
      const unresolvedAssets = Object.values(assets).filter(a => a.duration === -1 && a.url);
      if (unresolvedAssets.length > 0) {
        const results = await Promise.allSettled(
          unresolvedAssets.map(async (asset) => {
            const meta = await probeMedia(asset.url, asset.type || 'video');
            return { assetId: asset.id, duration: meta.duration };
          })
        );
        for (const result of results) {
          if (result.status !== 'fulfilled') continue;
          const { assetId, duration } = result.value;
          if (duration > 0) {
            assets[assetId] = { ...assets[assetId], duration };
            for (let i = 0; i < clips.length; i++) {
              if (clips[i].assetId === assetId && clips[i].duration === -1) {
                clips[i] = { ...clips[i], duration };
              }
            }
          }
        }
      }

      set({
        tracks: data.tracks?.length ? data.tracks : DEFAULT_TRACKS,
        assets,
        clips: clips.sort(sortByStart),
        mutedTracks: data.mutedTracks || {},
        trackVolumes: data.trackVolumes || {},
        assetsFolder: data.assetsFolder || [],
        currentAssetFolderId: null,
        timelineName: data.name || 'Untitled',
        episodeId,
        segmentId,
        playhead: 0,
        isPlaying: false,
        isDirty: false,
        lastSavedAt: data.updatedAt || null,
        isLoading: false,
      });

      // If we resolved any -1 durations, save the corrected values back to DB
      if (unresolvedAssets.length > 0) {
        const hasResolved = unresolvedAssets.some(a => assets[a.id].duration > 0);
        if (hasResolved) {
          await get().saveToDB();
        }
      }

      if (episodeId && segmentId) {
        const scriptAssets = Object.values(assets).filter(a => a.scriptId && a.sceneId);
        if (scriptAssets.length > 0) {
          const refreshed = { ...assets };
          await Promise.all(scriptAssets.map(async (asset) => {
            try {
              const res = await apiFetch(
                `/api/shows/${showId}/episodes/${episodeId}/segments/${segmentId}/scenes/${asset.sceneId}/script/${asset.scriptId}`
              );
              if (!res.ok) return;
              const script = await res.json();
              if (script.audio_url) {
                refreshed[asset.id] = {
                  ...refreshed[asset.id],
                  url: script.audio_url,
                  ...(script.audio_duration ? { duration: script.audio_duration / 1000 } : {}),
                  name: script.speech || script.text || refreshed[asset.id].name,
                  speech: script.speech || "",
                };
              }
            } catch (e) {
              console.error(`Failed to refresh script asset ${asset.id}:`, e);
            }
          }));
          set({ assets: refreshed });
        }
      }
    } catch (e) {
      console.error('Failed to load timeline:', e);
      set({ isLoading: false });
      throw e;
    }
  },

  saveToDB: async () => {
    const s = get();
    if (!s.showId || !s.timelineId) return;
    set({ isSaving: true });
    try {
      const payload = {
        name: s.timelineName,
        tracks: s.tracks,
        assets: s.assets,
        clips: s.clips,
        mutedTracks: s.mutedTracks,
        trackVolumes: s.trackVolumes,
        assetsFolder: s.assetsFolder,
        episode: s.episodeId || undefined,
        segment: s.segmentId || undefined,
      };
      const saved = await apiSaveTimeline(s.showId, s.timelineId, payload);
      set({ isSaving: false, isDirty: false, lastSavedAt: saved.updatedAt || null });
    } catch (e) {
      console.error('Failed to save timeline:', e);
      set({ isSaving: false });
      throw e;
    }
  },

  createAndLoad: async (showId, initialData = {}) => {
    set({ isLoading: true, showId });
    try {
      const payload = {
        name: initialData.name || 'New Timeline',
        tracks: DEFAULT_TRACKS,
        assets: {},
        clips: [],
        mutedTracks: {},
        trackVolumes: {},
        assetsFolder: [],
        episode: initialData.episodeId || undefined,
        segment: initialData.segmentId || undefined,
      };
      const created = await apiCreateTimeline(showId, payload);
      set({
        timelineId: created._id,
        tracks: created.tracks?.length ? created.tracks : DEFAULT_TRACKS,
        assets: created.assets || {},
        clips: created.clips || [],
        mutedTracks: created.mutedTracks || {},
        trackVolumes: created.trackVolumes || {},
        assetsFolder: created.assetsFolder || [],
        currentAssetFolderId: null,
        timelineName: created.name || 'Untitled',
        episodeId: created.episode || null,
        segmentId: created.segment || null,
        playhead: 0,
        isPlaying: false,
        isDirty: false,
        lastSavedAt: created.updatedAt || null,
        isLoading: false,
      });
      return created._id;
    } catch (e) {
      console.error('Failed to create timeline:', e);
      set({ isLoading: false });
      throw e;
    }
  },

  toJSON: () => {
    const s = get();
    return {
      version: 1,
      tracks: s.tracks,
      assets: s.assets,
      clips: s.clips,
      mutedTracks: s.mutedTracks,
      trackVolumes: s.trackVolumes,
    };
  },

  loadFromJSON: (data) => {
    if (!data || data.version !== 1) throw new Error("Bad timeline format");
    const clips = (data.clips || []).map(clip => ({
      ...clip,
      allowTrimming: clip.allowTrimming !== false,
      allowSplit: clip.allowSplit !== false,
    }));
    set({
      tracks: data.tracks || DEFAULT_TRACKS,
      assets: data.assets || {},
      clips: clips.sort(sortByStart),
      playhead: 0,
      isPlaying: false,
    });
  },
}));

